// ** React Imports
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import DatePresenter from "components/other/DatePresenter";
import DynamicLockIcon from "components/icons/DynamicLockIcon";
import UserMediaSkeletor from "components/user/UserMediaSkeletor";
import Link from "components/layout/Link";
import Checkbox from "components/layout/Checkbox";
import Image from "components/layout/Image";
import Tooltip from "components/layout/Tooltip";
import { ButtonPrimary } from "components/layout/Button/ButtonPrimary";
import AlbumLabel from "components/user/AlbumLabel";
import { StyledOverlay } from "styles/components/Overlay.styled";

// ** Third Party Components
import styled, { css } from "styled-components";
import loadable from "@loadable/component";
import cdnPrefixer from "helpers/cdnPrefixer";
import { toast } from "helpers/toastWrapper";

// ** Utils & Helpers
import generatePath from "utils/generatePath";
import { isDescriptionExists } from "utils/isDescriptionExists";
import api from "helpers/ApiClient";

// ** Styles Import
import { StyledBlockModerate } from "containers/Userpanel/styles";

// ** Lazy Loaded Components
const TextEditor = loadable(
	() => import("components/layout/TextEditor/TextEditor"),
	{ ssr: false }
);
const VideoPlayer = loadable(
	() => import("components/video/VideoPlayer/VideoPlayer"),
	{
		ssr: false,
	}
);

// #####################################################

const StyledImage = styled(Image)`
	max-width: 100%;
	max-height: 75vh;
`;

export const MediaWrapper = styled(Block)`
	display: flex;
	flex-direction: column;
	margin-bottom: 1rem;
	background-color: ${({ theme: { colors } }) => colors.whiteTrue};
	border-radius: var(--dz-sys-card-border-radius);
	box-shadow: var(--dz-sys-card-shadow);
	border: 1px solid var(--dz-sys-card-shadow-border);

	${({ $notAcceptedMedia }) =>
		$notAcceptedMedia &&
		css`
			background-color: ${({ theme: { colors } }) =>
				colors.buttonsDefaultGrey};
		`};
`;

export const MediaBody = styled(Block)`
	display: flex;
	flex-direction: column;
	padding: 0.75rem 1rem;
	overflow-wrap: anywhere;
`;

const MediaPostInEditView = ({ type, id, info, process }) => {
	const { t } = useTranslation(["userpanel"]);
	const currentLoggedUser = useSelector((state) => state.globalUser);
	const suspension = useSelector((state) => state.globalUser.suspension);
	const [whileEditing, setWhileEditing] = useState(false);
	const editor = useRef(null);
	const isBan = suspension?.duration > 0;
	const tooltipInfo = isBan ? t("suspensions:bannedAccount") : "";
	const albumTo = generatePath("/userpanel/gallery/albums/:id", {
		id: type === "photo" ? info.album : info.album_new,
	});

	return (
		<MediaWrapper
			$notAcceptedMedia={!info.accepted || info.ready === false}
		>
			<MediaBody>
				<UserMediaSkeletor
					alignCenter
					user={currentLoggedUser}
					size="small"
					content={
						<Text small color="grey">
							<Text mr={2}>
								<DatePresenter date={info.date_add} onlyDate />
							</Text>
							{type === "post" && info.private ? (
								<DynamicLockIcon />
							) : type === "post" && !info.private ? null : (
								<Link to={albumTo} underline iblock>
									<AlbumLabel
										isEllipsis
										name={info.album_name}
										private={info.private}
										amount={info?.media_in_album}
										color="primary"
										id={info?.album}
									/>
								</Link>
							)}
						</Text>
					}
					vipProps={{
						height: 14,
						marginBottom: 0.125,
					}}
				/>
				{type === "post" ? (
					<Block>
						<Block flex alignCenter>
							<TextEditor
								ref={editor}
								initValue={{
									plain: info.text_raw,
									parsed: info.text_edit,
								}}
								disabled={info?.isEdditing || isBan}
								acceptOnFirstRender
								tooltipInfo={tooltipInfo}
								placeholder={t("userpanel:gallery.text")}
							/>
						</Block>
						<Block flex alignCenter mt={4}>
							<Checkbox
								noPaddingX
								checked={info?.isPrivate || isBan}
								onChange={() =>
									info?.setIsPrivate(!info?.isPrivate)
								}
								disabled={info?.isEdditing}
								showLabel
								labelText={t("userpanel:posts.private")}
							/>
						</Block>
						<Block
							flex
							full
							justifyBetween={!info.accepted}
							justifyEnd={info.accepted}
						>
							{!info.accepted && (
								<Block flex alignCenter>
									<Text small color="grey">
										{t(
											"userpanel:waitingForModerate.common"
										)}
									</Text>
								</Block>
							)}
							<Tooltip
								text={tooltipInfo}
								wrapperStyle={{
									position: "relative",
									left: 0,
									right: 0,
									top: 0,
									bottom: 0,
								}}
							>
								<ButtonPrimary
									label={t("common:save")}
									onClick={() => {
										setWhileEditing(true);
										const { plain } =
											editor.current.serialize();

										api.put("/post", {
											id: Number(id),
											text: plain,
											private: info?.isPrivate,
										})
											.then(() => {
												setWhileEditing(false);
												toast.success(
													t(
														"userpanel:posts.postSaved"
													)
												);
												process();
											})
											.catch((error) => {
												setWhileEditing(false);
												if (
													error?.response?.data?.error
												) {
													toast.error(
														`${error.response.data.error}`
													);
												}
											});
									}}
									disabled={info?.isEdditing || isBan}
								/>
							</Tooltip>
						</Block>
					</Block>
				) : (
					<>
						<Block flex justifyCenter relative>
							{(type === "video" && info.ready === false) ||
							info.media_type === "video_convert" ? (
								<Block>
									{t(
										"userpanel:videos.conversion.inProgress"
									)}
								</Block>
							) : type === "video" ? (
								<Block flex full column>
									<VideoPlayer
										source={{
											src: info.video,
											type: "video/mp4",
										}}
										poster={info.poster}
										options={{
											autoPlay: false,
											playsInline: true,
										}}
										styleOptions={{
											playIconSize:
												"userpanel_edit_video",
										}}
										activeResizeObserver
									/>
								</Block>
							) : type === "photo" ? (
								<Block flex full justifyCenter>
									<StyledImage
										src={cdnPrefixer(info.image)}
									/>
									<StyledOverlay />
								</Block>
							) : null}
						</Block>
						<StyledBlockModerate
							moderate={
								!info.accepted_description &&
								isDescriptionExists(info.description) &&
								info.accepted
							}
							mt={6}
						>
							<TextEditor
								ref={editor}
								initValue={{
									plain: info.description_raw,
									parsed: info.description_edit,
								}}
								disabled={isBan}
								tooltipInfo={tooltipInfo}
								placeholder={t("userpanel:gallery.text")}
							/>
							<Block
								mt={4}
								flex
								justifyBetween={
									(!info.accepted_description ||
										info.ready === false) &&
									isDescriptionExists(info.description)
								}
								justifyEnd={
									(info.accepted_description &&
										info.ready !== false) ||
									!isDescriptionExists(info.description)
								}
								full
							>
								{(!info.accepted_description ||
									info.ready === false) &&
									isDescriptionExists(info.description) && (
										<Block flex alignCenter>
											<Text small color="grey">
												{t(
													"userpanel:waitingForModerate.common"
												)}
											</Text>
										</Block>
									)}
								<Tooltip
									text={tooltipInfo}
									wrapperStyle={{
										position: "relative",
										left: 0,
										right: 0,
										top: 0,
										bottom: 0,
									}}
								>
									<ButtonPrimary
										onClick={() => {
											setWhileEditing(true);
											const { plain } =
												editor.current.serialize();

											if (type === "video") {
												api.put("/video", {
													video: Number(id),
													album: info.album,
													description: plain,
												})
													.then(() => {
														setWhileEditing(false);
														toast.success(
															t(
																"userpanel:posts.postSaved"
															)
														);
														process();
													})
													.catch((error) => {
														setWhileEditing(false);
														if (
															error?.response
																?.data?.error
														) {
															toast.error(
																`${error.response.data.error}`
															);
														}
													});
											} else if (type === "photo") {
												api.put("/photo", {
													photo: Number(id),
													album: info.album,
													description: plain,
												})
													.then(() => {
														setWhileEditing(false);
														toast.success(
															t(
																"userpanel:posts.postSaved"
															)
														);
														process();
													})
													.catch((error) => {
														setWhileEditing(false);
														if (
															error?.response
																?.data?.error
														) {
															toast.error(
																`${error.response.data.error}`
															);
														}
													});
											}
										}}
										label={t("common:save")}
										disabled={isBan || whileEditing}
									/>
								</Tooltip>
							</Block>
						</StyledBlockModerate>
					</>
				)}
			</MediaBody>
		</MediaWrapper>
	);
};

export default MediaPostInEditView;
